import arrayToTree from 'array-to-tree';
import { get, uniq, size, orderBy, flatten } from 'global/lib/lodash';
import { waitIdle } from 'global/utils';

export const getId = (item) => {
  if (!item) {
    return -1;
  }
  return item._id || item.id;
};

export const getItemType = (item) => {
  let type = '';
  if (!item) {
    return 'none';
  }

  const isGobs = isGob(item);

  if (item.type) {
    type = item.type;
  } else if (isGobs) {
    type = 'checkpoint';
  } else if (item.checkpointId) {
    type = 'checkpoint';
  } else if (item.checklistId) {
    type = 'checklist';
  } else if (item.systemId) {
    type = 'system';
  } else if (item.supersetId) {
    type = 'superset';
  } else if (item.selectAll !== undefined) {
    type = 'selectAll';
  }

  return type;
};

export const getItemKey = (item) => {
  return `${getItemType(item)}_${getId(item)}`;
};

export const isGob = (item) => {
  return get(item, 'depth') === 2 && !get(item, 'children') && !item.isSubScore ? true : false;
};

export const filterData = async (d, filter) => {
  await waitIdle();
  const { value, hideNa, search, listGroup, onlyLoc, assessment } = filter;
  const locationValuesOnly = listGroup === 'location' || onlyLoc;
  const isCatLoc = listGroup === 'category' && onlyLoc;
  const currentFilt = value;
  const searchLower = search ? search.toLowerCase() : '';
  let hasChildren = [];
  let subscores = [];
  if (currentFilt !== undefined && (currentFilt !== 'any' || locationValuesOnly || hideNa || search)) {
    await waitIdle();

    const itemPassesFilters = (item) => {
      let passes = true;
      if ((hideNa || listGroup === 'location') && item.displayScore === 'N/A') {
        passes = false;
      } else {
        const crit = ((get(item, 'name') || '') + (get(item, 'criteria') || '') + (get(item, 'checklist.name') || '')).toLowerCase();
        if (item.filters && currentFilt !== 'any') {
          // search with button filters
          const defFilts = ['high', 'low', 'act'];
          if (defFilts.indexOf(currentFilt) > -1) {
            passes = get(item, `filters.${currentFilt}`) && (!search || crit.indexOf(searchLower) !== -1);
          } else {
            //filter is using the select dropdown
            const { value, calculatedScore } = item;
            const score = currentFilt === -1 ? value : calculatedScore; //for some reason neutral comes over as a calculated score as 1
            if (score === null) {
              passes = (currentFilt === 'na') && (!search || crit.indexOf(searchLower) !== -1);
            } else {
              passes = (parseInt(score) === currentFilt) && (!search || crit.indexOf(searchLower) !== -1);
            }
          }
        } else if ((item.depth === 3 || (item.depth === 2 && item.scorecardScoreId)) && search && crit.indexOf(searchLower) === -1) {
          // search without button filters, catch general observations
          passes = false;
        }
      }
      return passes;
    };

    const items = d.filter((item, i) => {
      let passes = itemPassesFilters(item);
      if (isCatLoc && size(item.subscores)) {
        passes = !!size(item.subscores);
      } else if (listGroup === 'category' && size(item.subscores)) {
        passes = !!size(item.subscores.filter(itemPassesFilters));
      }
      if (item.subscores) {
        subscores = [...subscores, ...item.subscores.filter(itemPassesFilters)];
      }
      return passes;
    });

    await waitIdle();

    if (listGroup === 'location') {
      // const subscoreRooms = uniq((subscores || []).map((i) => get(i,'room')));
      const scorecardName = get(
        items.find((i) => !get(i, 'parentId')),
        'name',
        'Scorecard Name'
      );
      const assessmentName = get(assessment, 'data.realm.name', 'Location');
      const subscoreRooms = (subscores || []).reduce((agg, i) => {
        const room = i.realm;

        const found = agg.findIndex((a) => a.id == get(i, 'realmId'));

        if (found !== -1) {
          get(agg, `${found}.children`).push(i);
        } else if (!!room) {
          agg.push({ ...room, children: [i] });
        }

        return agg;
      }, []);

      const scoreDict = {
        scorecardId: '',
        assessmentId: '',
      };
      hasChildren = [
        {
          ...scoreDict,
          name: scorecardName,
          displayScore: size(subscores),
          depth: 0,
          id: 'realm1',
          type: 'selectAll',
          children: [
            {
              ...scoreDict,
              name: assessmentName,
              depth: 0,
              id: '1',
              type: 'system',
              displayScore: size(subscores),
              children: subscoreRooms.map((room, i) => ({
                ...scoreDict,
                id: room.id,
                name: get(room, 'name', 'none'),
                displayScore: size(get(room, 'children')),
                type: 'room',
                depth: 1,
                order: i,
                parentId: 1,
                children: get(room, 'children', []).map((c) => ({ ...c, parentId: room.id })),
              })),
            },
          ],
        },
      ];
    } else {
      const filteredTree = arrayToTree(items, { parentProperty: 'parentId' });
      hasChildren = orderBy(filteredTree, ['order']).filter((a) => {
        a.children = orderBy(get(a, 'children', []), ['order', 'name']).filter((af) => {
          af.children = orderBy(get(af, 'children', []), ['order']).filter((ac) => {
            const childs = get(ac, 'children', []);
            ac.children = orderBy(childs, ['name']).sort((a, b) => {
              if (!a.checkpointId || !b.checkpointId) {
                return -1;
              }
              return 0;
            });

            return isCatLoc ? !!size(flatten(ac.children.map((cc) => cc.subscores || []))) : !!size(childs);
          });
          return !!size(af.children);
        });
        return !!size(a.children);
      });
    }
  } else {
    if (listGroup === 'location') {
      // const subscoreRooms = uniq((subscores || []).map((i) => get(i, 'room')));
      hasChildren = [];
    } else {
      hasChildren = arrayToTree(d, { parentProperty: 'parentId' });
    }
  }

  return {
    hasChildren,
  };
};

export const findAllSubs = item => {
  if (item.subScores) {
    return flatten([...item.subScores]).filter(f => !!f);
  }
  return [];
}

export const isSelectable = (item) => {
  if (item.value === null) {
    return false;
  }

  const value = parseInt(item.value);

  if (value === 100 || value === -1) {
    const hasImage = !!size(item.images);
    const hasNotes = !!size(item.notes);
    const isGobs = isGob(item);
    const subs = findAllSubs(item);
    const hasSubs = !!size(subs);

    if (!hasImage && !hasNotes && !isGobs && !hasSubs) {
      return false;
    }
  }

  return true;
}